import { combineLatest, from, Observable, merge } from 'rxjs';
import { debounceTime, map, catchError, mergeMap } from 'rxjs/operators';
import * as momentImported from 'moment';
import * as localForageImported from 'localforage';
import { extendPrototype } from 'localforage-observable';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

class Cache {
    getCollection(collection) {
        return combineLatest(collection).pipe(debounceTime(10));
    }
}

const moment = momentImported;
const localForage = localForageImported;
class CacheService {
    constructor() {
        this.db$ = from(localForage.ready()).pipe(map(x => {
            extendPrototype(localForage);
            const db = localForage;
            db.newObservable.factory = fn => Observable.create(fn);
            db.config({
                version: 1.0,
                name: "ESL Play",
                storeName: "ESL_PLAY",
                description: "",
                driver: localForage.LOCALSTORAGE,
            });
            db.configObservables({ crossTabNotification: true });
            return db;
        }), catchError(error => {
            this.handleError();
            return [null];
        }));
        this.cleanUp();
    }
    /**
     * @param object
     */
    isExpired(object) {
        if (object == null) {
            return false;
        }
        const now = moment.utc().format("X");
        const expires = object.ttl;
        return now > expires;
    }
    /**
     * @param key
     * @param item
     */
    getItemContent(key, item) {
        if (item !== null && item.hasOwnProperty("newValue")) {
            item = item.newValue;
        }
        if (item !== null && !this.isExpired(item)) {
            return item.content;
        }
        if (this.isExpired(item)) {
            this.removeItem(key);
        }
        return null;
    }
    /**
     * @param key
     */
    getObservable(key) {
        return this.db$.pipe(mergeMap(db => {
            if (!db) {
                this.handleError();
            }
            return merge(db.newObservable({
                key: key,
                setItem: true,
                removeItem: false,
                clear: false,
                changeDetection: false,
            }).pipe(map((item) => this.getItemContent(key, Object.assign({}, item))), catchError(() => {
                this.handleError();
                return [];
            })), from(this.getItem(key)));
        }));
    }
    /**
     * @param key
     */
    getItem(key) {
        return this.db$.toPromise().then(db => {
            if (!db) {
                this.handleError();
            }
            return db
                .getItem(key)
                .then((item) => {
                return this.getItemContent(key, item);
            })
                .catch(this.handleError);
        });
    }
    /**
     * @param key
     * @param value
     * @param timeout
     */
    setItem(key, value, timeout = null) {
        const ttl = timeout || moment.utc().add(10, "minutes");
        const item = { ttl: ttl.format("X"), content: value };
        return this.db$.toPromise().then(db => {
            if (!db) {
                this.handleError();
            }
            return db
                .setItem(key, item)
                .then(() => {
                return value;
            })
                .catch(this.handleError);
        });
    }
    /**
     * @param key
     */
    removeItem(key) {
        return this.db$.toPromise().then(db => {
            if (!db) {
                this.handleError();
            }
            return db.removeItem(key).catch(this.handleError);
        });
    }
    /**
     * Cleans up the expired caches
     */
    cleanUp() {
        return this.db$.toPromise().then(db => {
            if (!db) {
                this.handleError();
            }
            db.iterate((item, key) => {
                if (item === null || this.isExpired(item)) {
                    this.removeItem(key);
                }
            }).catch(this.handleError);
        });
    }
    handleError(error) {
        if (error && error.message.includes("Refresh the page")) {
            location.reload();
        }
        return;
    }
}
CacheService.ɵfac = function CacheService_Factory(t) { return new (t || CacheService)(); };
CacheService.ɵprov = i0.ɵɵdefineInjectable({ token: CacheService, factory: CacheService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CacheService, [{
        type: Injectable
    }], function () { return []; }, null); })();

class CacheModule {
}
CacheModule.ɵfac = function CacheModule_Factory(t) { return new (t || CacheModule)(); };
CacheModule.ɵmod = i0.ɵɵdefineNgModule({ type: CacheModule });
CacheModule.ɵinj = i0.ɵɵdefineInjector({ providers: [CacheService], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CacheModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CacheModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [CacheService],
            }]
    }], null, null); })();

/*
 * Public API Surface of cache
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Cache, CacheModule, CacheService };
//# sourceMappingURL=play-cache.js.map
